import React, { memo } from 'react';
import { Position } from 'reactflow';
import { Tooltip } from 'antd';
import {
    MailOutlined,
    ApiOutlined,
    EditOutlined,
    RocketOutlined
} from '@ant-design/icons';
import EnhancedHandle from './EnhancedHandle';

const ActionNode = ({ data, isConnectable, selected }) => {
    // Determine action type and configuration details
    const getActionInfo = () => {
        const type = data.actionType || data.config?.type;

        switch (type) {
            case 'email':
                const recipients = data.config?.recipients || [];
                const recipientList = recipients.map(r => r.email).join(', ');
                const subject = data.config?.subject || '';

                return {
                    type: 'email',
                    label: 'Send Email',
                    icon: <MailOutlined style={{ fontSize: 16, marginRight: 8 }} />,
                    color: '#1890ff',
                    bgColor: '#e6f7ff',
                    headerBg: '#1890ff',
                    displayText: [
                        { label: 'To:', value: recipientList || 'No recipients' },
                        { label: 'Subject:', value: subject }
                    ],
                    description: `Send email with subject "${subject}" to ${recipientList || 'no recipients'}`
                };

            case 'webhook':
                const url = data.config?.url || '';
                const method = data.config?.method || 'POST';

                return {
                    type: 'webhook',
                    label: 'Web Hook',
                    icon: <ApiOutlined style={{ fontSize: 16, marginRight: 8 }} />,
                    color: '#f759ab',
                    bgColor: '#fff0f6',
                    headerBg: '#f759ab',
                    displayText: [
                        { label: 'URL:', value: url || 'No URL set' },
                        { label: 'Method:', value: method }
                    ],
                    description: `${method} request to ${url || 'URL not set'}`
                };

            case 'updateRecord':
                const updateType = data.config?.updateType || 'javascript';
                const script = data.config?.script || '';

                return {
                    type: 'updateRecord',
                    label: 'Update Record',
                    icon: <EditOutlined style={{ fontSize: 16, marginRight: 8 }} />,
                    color: '#52c41a',
                    bgColor: '#f6ffed',
                    headerBg: '#52c41a',
                    displayText: [
                        { label: 'Type:', value: updateType },
                        { label: 'Update:', value: updateType === 'javascript' ?
                                (script.length > 25 ? script.substring(0, 22) + '...' : script) :
                                'AI-generated updates' }
                    ],
                    description: `Update record using ${updateType}`
                };

            default:
                return {
                    type: 'action',
                    label: 'Action',
                    icon: <RocketOutlined style={{ fontSize: 16, marginRight: 8 }} />,
                    color: '#722ed1',
                    bgColor: '#f9f0ff',
                    headerBg: '#722ed1',
                    displayText: [
                        { label: 'Type:', value: 'Custom Action' }
                    ],
                    description: 'Perform a custom action'
                };
        }
    };

    const actionInfo = getActionInfo();

    // Show execution count badge if available
    const ExecutionCountBadge = () => {
        if (data.executionCount === undefined) return null;

        return (
            <div style={{
                position: 'absolute',
                top: -8,
                right: -8,
                background: '#1890ff',
                color: 'white',
                borderRadius: '50%',
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                zIndex: 10
            }}>
                {data.executionCount}
            </div>
        );
    };

    return (
        <Tooltip title={actionInfo.description}>
            <div
                style={{
                    width: 220,
                    position: 'relative',
                    background: selected ? actionInfo.bgColor : 'white',
                    border: selected ? `1px solid ${actionInfo.color}` : '1px solid #d9d9d9',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                    fontFamily: 'Arial, sans-serif'
                }}
            >
                <ExecutionCountBadge />

                {/* Header */}
                <div style={{
                    background: actionInfo.headerBg,
                    color: 'white',
                    padding: '8px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: '1px solid #d9d9d9'
                }}>
                    {actionInfo.icon}
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                        {data.label || actionInfo.label}
                    </div>
                </div>

                {/* Content */}
                <div style={{ padding: '12px 16px' }}>
                    {actionInfo.displayText.map((item, index) => (
                        <div key={index} style={{
                            fontSize: '13px',
                            display: 'flex',
                            marginBottom: index < actionInfo.displayText.length - 1 ? '8px' : 0
                        }}>
                            <div style={{
                                fontWeight: 'bold',
                                marginRight: '4px',
                                minWidth: '60px'
                            }}>
                                {item.label}
                            </div>
                            <div style={{
                                color: '#333',
                                wordBreak: 'break-word',
                                fontFamily: actionInfo.type === 'updateRecord' && index === 1 ? 'monospace' : 'inherit'
                            }}>
                                {item.value}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Input handle - enhanced */}
                <EnhancedHandle
                    type="target"
                    position={Position.Top}
                    id="in"
                    style={{
                        top: -6
                    }}
                    color={actionInfo.color}
                    isConnectable={isConnectable}
                />

                {/* Output handle - enhanced */}
                <EnhancedHandle
                    type="source"
                    position={Position.Bottom}
                    id="out"
                    style={{
                        bottom: -6
                    }}
                    color={actionInfo.color}
                    isConnectable={isConnectable}
                />
            </div>
        </Tooltip>
    );
};

export default memo(ActionNode);
